import {
  GPS_MAX_ACCURACY_METERS,
  isGpsPoint,
  type GpsPoint,
} from "../../domain/gps/gps-distance.js";
import { isGpsClub, type GpsClub } from "../../shared/contracts/gps-shot.js";

export const GPS_DRAFT_KEY = "fairway-gps-shot-draft-v1";
export const GPS_DRAFT_LIFETIME_MS = 60 * 60 * 1_000;
export interface GpsDraft {
  id: string;
  accountId: string;
  roundId: string;
  participantId: string;
  holeNumber: number;
  expiresAt: number;
  start: GpsPoint;
  end?: GpsPoint;
  club?: GpsClub | null | undefined;
}
export type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
export function gpsDraftStorage(): DraftStorage | undefined {
  try {
    return sessionStorage;
  } catch {
    return undefined;
  }
}

export function readGpsDraft(
  storage: DraftStorage | undefined,
  accountId: string,
  roundId: string,
  participantId: string,
  now = Date.now(),
): GpsDraft | null {
  try {
    const value: unknown = JSON.parse(
      storage?.getItem(GPS_DRAFT_KEY) ?? "null",
    );
    if (
      !validDraft(value, now) ||
      value.accountId !== accountId ||
      value.roundId !== roundId ||
      value.participantId !== participantId
    ) {
      storage?.removeItem(GPS_DRAFT_KEY);
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

export function writeGpsDraft(
  storage: DraftStorage | undefined,
  draft: GpsDraft | null,
): boolean {
  if (!storage) return false;
  try {
    if (draft) storage.setItem(GPS_DRAFT_KEY, JSON.stringify(draft));
    else storage.removeItem(GPS_DRAFT_KEY);
    return true;
  } catch {
    return false;
  }
}

export function clearGpsDraft(storage = gpsDraftStorage()): void {
  writeGpsDraft(storage, null);
}

/** Called on account selection so another account never inherits raw endpoints. */
export function selectGpsDraftAccount(
  accountId: string | null,
  storage = gpsDraftStorage(),
): void {
  try {
    const value: unknown = JSON.parse(
      storage?.getItem(GPS_DRAFT_KEY) ?? "null",
    );
    if (!validDraft(value, Date.now()) || value.accountId !== accountId)
      clearGpsDraft(storage);
  } catch {
    clearGpsDraft(storage);
  }
}

function validDraft(value: unknown, now: number): value is GpsDraft {
  if (!value || typeof value !== "object") return false;
  const draft = value as GpsDraft;
  return (
    typeof draft.id === "string" &&
    /^[0-9a-f-]{36}$/i.test(draft.id) &&
    typeof draft.accountId === "string" &&
    typeof draft.roundId === "string" &&
    typeof draft.participantId === "string" &&
    Number.isInteger(draft.holeNumber) &&
    draft.holeNumber >= 1 &&
    draft.holeNumber <= 18 &&
    Number.isFinite(draft.expiresAt) &&
    draft.expiresAt > now &&
    draft.expiresAt <= now + GPS_DRAFT_LIFETIME_MS &&
    (draft.club == null || isGpsClub(draft.club)) &&
    isGpsPoint(draft.start) &&
    draft.start.accuracy <= GPS_MAX_ACCURACY_METERS &&
    (draft.end === undefined ||
      (isGpsPoint(draft.end) && draft.end.accuracy <= GPS_MAX_ACCURACY_METERS))
  );
}
